import type { Lead } from './types';
import type { LeadClosureReason } from './leadFields';

/**
 * Who may be put into a campaign list, and who may not.
 *
 * ── Why this file exists at all ─────────────────────────────────────────────
 *
 * Drop 38 gives the Leads table a bulk **Add to list**, which is the half of
 * Kyu's loop that was missing: "then we can see those people and set up
 * campaigns, put them in a list, send emails again... basic sales."
 *
 * Measured before building it: `addLeadsToList` upserts memberships with no
 * check of any kind. Nothing anywhere in the app stops a lead closed as **Do
 * not contact** from being added to a list and emailed. That hole was narrow
 * while the only door was the Lists tab — reaching the mutation meant
 * searching for somebody by name, so you would have had to go and find the
 * person who asked you to stop. A bulk add from a filtered set widens it to
 * one click, so the guard ships in the same drop as the button.
 *
 * ── The two cases are not the same case ────────────────────────────────────
 *
 * **Do not contact is consent**, and it is not the sender's to override, so
 * those leads are removed here and the count is reported. **A bad address is
 * deliverability** — the measured bounce rate is 13.2% — so those leads are
 * added and merely counted. Whether to send to an address that bounced last
 * time is a judgement about a campaign; whether to email somebody who asked
 * you to stop is not a judgement at all.
 *
 * ── Read from the row, never from the spelling ─────────────────────────────
 *
 * The block is `lead_closure_reasons.blocks_contact` (20261219000100), not
 * `name === 'Do not contact'`. The two fail in opposite directions: rename the
 * reason in Admin and a name match silently stops protecting anybody, the list
 * still looks right, and the first anybody hears of it is a complaint. A
 * column survives the rename, and a sixth reason that also means stop —
 * Unsubscribed, a legal request — is one UPDATE rather than a new string in
 * this file.
 */

/**
 * Does this one lead's closing reason mean we were asked to stop?
 *
 * Returns **null** when the answer is not knowable — the vocabulary has not
 * loaded, or its rows arrived without the flag. Same refusal discipline as
 * `splitForList`, and for the same reason: `undefined` is not `false`, and a
 * check that quietly answers "no" when it means "I don't know" is how Drop 38
 * shipped a guard that protected nobody.
 *
 * For the single-lead controls, which want to explain BEFORE the click rather
 * than report an absence afterwards. Since 20261220000100 the database is what
 * actually stops the write, so a caller that gets `null` here should say
 * nothing and let the trigger do its job — never open the gate.
 */
export function blocksContact(
  lead: Pick<Lead, 'closure_reason_id'>,
  reasons: LeadClosureReason[],
): boolean | null {
  if (reasons.length === 0 || reasons.some((r) => typeof r.blocks_contact !== 'boolean')) {
    return null;
  }
  if (lead.closure_reason_id === null) {
    return false;
  }
  return reasons.some((r) => r.id === lead.closure_reason_id && r.blocks_contact);
}

export interface ListAddSplit {
  /** The leads to actually add. */
  allowed: string[];
  /** How many were removed because their closing reason blocks contact. */
  blocked: number;
  /** How many of `allowed` have a known-bad address. Reported, not removed. */
  badAddress: number;
}

/**
 * Split a selection into who gets added and who does not.
 *
 * Returns **null** when `reasons` is empty, which means the vocabulary has not
 * loaded — and that is the point of the return type. There is no honest answer
 * to "does this lead's reason block contact" without the reasons, and the two
 * ways of guessing are both wrong: assume none block and the guard is off
 * exactly when it is least visible; assume all do and a re-engagement campaign
 * silently adds nobody. So the question is refused, and the caller has to
 * disable the button until it can be answered.
 *
 * `lead_closure_reasons` holds seven rows — five seeded by 20261122000100, two
 * added by 20261218000100 — and the harness asserts they are there, so an empty
 * list in the running app never means "there are no reasons".
 */
export function splitForList(
  leads: Lead[],
  selectedIds: ReadonlySet<string>,
  reasons: LeadClosureReason[],
): ListAddSplit | null {
  if (reasons.length === 0) {
    return null;
  }
  // The flag must actually be ON THE ROW, not merely typed as present.
  //
  // This check exists because Drop 38 shipped without it and the guard
  // protected nobody: `fetchClosureReasons` selected a column list that did
  // not include `blocks_contact`, so every row arrived with the field
  // `undefined` while TypeScript believed it was a boolean. `undefined` is
  // falsy, so the blocking set came out empty and every Do-not-contact lead
  // was cleared for a campaign list — silently, with every unit test green,
  // because the fixtures set a field the wire did not carry.
  //
  // `undefined` and `false` mean completely different things here — "nobody
  // told me" versus "this reason does not block" — so they must not collapse
  // into the same branch. Refusing turns an invisible failure into a disabled
  // control somebody can see and report.
  if (reasons.some((reason) => typeof reason.blocks_contact !== 'boolean')) {
    return null;
  }
  const blocking = new Set(
    reasons.filter((reason) => reason.blocks_contact).map((reason) => reason.id),
  );
  const allowed: string[] = [];
  let blocked = 0;
  let badAddress = 0;
  for (const lead of leads) {
    if (!selectedIds.has(lead.id)) {
      continue;
    }
    if (lead.closure_reason_id !== null && blocking.has(lead.closure_reason_id)) {
      blocked += 1;
      continue;
    }
    allowed.push(lead.id);
    if (lead.bad_contact) {
      badAddress += 1;
    }
  }
  return { allowed, blocked, badAddress };
}

/**
 * What the person is told after the add, in the shape the bulk bar already
 * uses for its partial results.
 *
 * Every number that is not zero is said out loud, and the sentence never
 * implies more than happened: `added` is what the mutation actually inserted,
 * so a lead already on the list is not counted twice — the upsert skips it and
 * the difference shows up as "already on it".
 */
export function listAddMessage(added: number, split: ListAddSplit, listName: string): string {
  const parts = [`Added ${String(added)} to ${listName}.`];
  const already = split.allowed.length - added;
  if (already > 0) {
    parts.push(`${String(already)} ${already === 1 ? 'was' : 'were'} already on it.`);
  }
  if (split.blocked > 0) {
    parts.push(
      `${String(split.blocked)} ${split.blocked === 1 ? 'was' : 'were'} left out — Do not contact.`,
    );
  }
  if (split.badAddress > 0) {
    parts.push(
      `${String(split.badAddress)} of these ${split.badAddress === 1 ? 'has' : 'have'} a bad address on file.`,
    );
  }
  return parts.join(' ');
}
